<script lang="ts">
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import StateSettingsStore from "@shared/svelte/Stores/StateSettingsStore";
  import Spinner from "@shared/svelte/Components/SpinnerInternal.svelte";
  import DistributorsStore from "@shared/svelte/Stores/DistributorsStore";
  import RetailerFulfillmentMethodsStore from "@retailer/svelte/stores/RetailerFulfillmentMethodsStore";
  import distributorsServiceFactory from "@shared/services/distributors";
  import qService from "@modern/services/angularjs_shims/q_service";
  import flashFactory from "@shared/services/flash";
  import cart from "@modern/services/cart";
  import { params } from "@roxi/routify";
  import CartLayout from "@retailer/svelte/Cart/Page/CartLayout.svelte";
  import Flash from "@shared/services/flashv2.js";
  import { restrictRepAccess } from "@retailer/services/preview_forbidden_flash";
  import ConnectDistributorModal from "@retailer/svelte/Settings/DistributorContacts/ConnectDistributorModal/ConnectDistributorModal.svelte";
  import type { Distributor } from "@shared/types/Distributor";
  import { getControlStateAuthenticationService } from "@retailer/services/retailer_user_authentications_service";

  const Distributors = distributorsServiceFactory(null, flashFactory(), qService);
  let Cart;
  let callbacks: any = {};

  const authService = getControlStateAuthenticationService();

  callbacks.setErrors = (err: string) => {
    Flash.now({ message: err, type: "error" });
  };

  const openConnectDistributorModal = (selectedDistro: Distributor): void => {
    if (restrictRepAccess()) return;

    const addSalesRepErrorCallback = res => {
      if (res.data && res.data.errors) {
        callbacks.setErrors(res.data.errors.messages.join("\n"));
        return;
      }
      callbacks.setErrors("Sorry - we had a problem. Please try again or contact support.");
    };

    const submitErrorCallback = res => {
      callbacks.setErrors(res.data);
    };

    window["svelteModal"](ConnectDistributorModal, {
      submit: Distributors.submit,
      addNewSalesRep: Distributors.addNewSalesRep,
      providedDistributor: true,
      showConnectDistributorModal: true,
      addSalesRepErrorCallback,
      submitErrorCallback,
      selectedDistributor: selectedDistro,
    });
  };

  const managersRes = async () =>
    HttpPromise({
      method: "GET",
      url: "/api/retailer/user/managers",
    });

  const stateSettingsRes = async () => {
    const response = await HttpPromise({
      method: "GET",
      url: "/api/retailer/state_settings",
    });

    StateSettingsStore.set(response);
    return response;
  };

  const distributorsRes = async () => {
    const response = await HttpPromise({
      method: "GET",
      url: "/api/retailer/distributors",
      data: {
        with_cutoff_time: true,
        with_domains: true,
        with_integrated: true,
        with_sales_reps: true,
      },
    });

    DistributorsStore.set(response);
  };

  const load = async () => {
    const managers = managersRes();
    const stateSettings = stateSettingsRes();
    const distributors = distributorsRes();
    const cartLoad = cart.initialize();
    const fulfillmentMethods = RetailerFulfillmentMethodsStore.refreshFulfillmentMethods();

    const results = await Promise.all([
      managers,
      stateSettings,
      distributors,
      cartLoad,
      fulfillmentMethods,
    ]);
    Cart = cart.getCartService();
    return {
      managers: results[0],
    };
  };

  const reloadCart = () => {
    Cart.load();
  };
</script>

{#await load()}
  <Spinner />
{:then data}
  <CartLayout
    {callbacks}
    {openConnectDistributorModal}
    cartService={Cart}
    managers={data.managers || []}
    connectSalesRep={$params.connectSalesRep}
  />
{/await}

{#if authService?.authenticationModalComponent}
  <svelte:component this={authService?.authenticationModalComponent} on:update={reloadCart} />
{/if}
